import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { AuthenticationService } from '../../../core/service/auth/authentication.service';
import { LoaderService } from '../../../core/service/loader.service';
import { PopupServiceService } from '../../../core/service/popup/popup-service.service';
import { LocalStorageService } from '../../../core/service/localStorage/local-storage.service';
import { Router } from '@angular/router';
import moment from 'moment';
import { DomainConfigService } from '../../../core/service/domainConfig/domain-config.service';
import { ProfileService } from '../../../core/service/profile/profile.service';
import { InternalChatService } from '../../../core/service/internalChat/internal-chat.service';
import { TmsSocketService } from '../../../core/service/tms-socket/tms-socket.service';
import { TMSService } from '../../../core/service/tms/tms.service';
import { AwayStatusComponent } from '../../../shared/away-status/away-status.component';
import { TodayOverviewDialogComponent } from '../../../shared/today-overview-dialog/today-overview-dialog.component';
import { TodayOverviewService } from '../../../shared/today-overview/today-overview.service';

interface LoginPayload {
  email: string;
  password: string;
  currentVersion: string;
  remember: string;
  security: string;
  newSetup?: boolean;
  newPortal: string;
  source?: string;
}

@Component({
  selector: 'app-authenticator',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatInputModule,
    SharedModule
  ],
  templateUrl: './authenticator.component.html',
  styleUrl: './authenticator.component.scss'
})
export class AuthenticatorComponent {
  brandLogo = './assets/images/logo.svg';

  fb = inject(FormBuilder);
  router = inject(Router);
  auth = inject(AuthenticationService);
  loader = inject(LoaderService);
  popup = inject(PopupServiceService);
  localStorageService = inject(LocalStorageService);
  private domainConfigService = inject(DomainConfigService);
  private profileService = inject(ProfileService);
  private internalChatService = inject(InternalChatService);
  private tmsSocketService = inject(TmsSocketService);
  private tmsService = inject(TMSService);
  private dialog = inject(MatDialog);
  private todayOverviewService = inject(TodayOverviewService);

  qrCode!: string;
  email!: string;
  password!: string;
  remember!: string;
  boxes = Array(6).fill(0); // 6 digit inputs
  codeArray = ['', '', '', '', '', '']; // stores each digit
  securityForm!: FormGroup;
  currentYear = moment().format('YYYY');
  userDetailsData: any = null; // Store user details data
  isAllSelected = false; // Track if ctrl+a was pressed

  // Domain-specific styling
  buttonBgColor = '#FF6525';
  buttonTextColor = '#ffffff';
  buttonDisabledBgColor = 'rgba(26, 27, 31, 0.12)';
  buttonDisabledTextColor = 'rgba(26, 27, 31, 0.38)';
  companyName = 'Crizac Ltd';

  constructor() {
    // Setup domain branding
    this.setupDomainBranding();

    const nav = inject(Router).getCurrentNavigation();
    const state = nav?.extras?.state;

    if (!state || !state['email'] || !state['password']) {
      this.router.navigate(['/login']);
      return;
    }

    this.email = state['email'];
    this.password = state['password'];
    this.qrCode = state['qrCode'];
    this.remember = state['remember'];

    this.securityForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    if (!this.email || !this.password) {
      console.log('Redirecting to /login');
      this.router.navigate(['/login']);
    }
  }

  /**
   * Setup domain-specific branding (logo, button colors, company name)
   */
  private setupDomainBranding(): void {
    this.brandLogo = this.domainConfigService.getLoginLogo();
    this.buttonBgColor = this.domainConfigService.getButtonBgColor();
    this.buttonTextColor = this.domainConfigService.getButtonTextColor();
    this.buttonDisabledBgColor = this.domainConfigService.getButtonDisabledBgColor();
    this.companyName = this.domainConfigService.getCompanyName();
  }

  /**
   * Check if form is valid (all 6 digits entered)
   */
  get isFormValid(): boolean {
    return this.codeArray.every(digit => digit !== '') &&
      this.codeArray.join('').length === 6;
  }

  submitCode() {
    if (this.isFormValid) {
      const payload: LoginPayload = {
        email: this.email,
        password: this.password,
        currentVersion: '0.8.98',
        remember: this.remember,
        security: this.codeArray.join(''),
        newPortal: "true",
        source: "web",
      };

      if (this.qrCode) {
        payload.newSetup = true;
      }

      this.loader.show();
      this.auth.login(payload).subscribe({
        next: (res: any) => {
          this.loader.hide();

          if (res?.data?.token) {
            this.localStorageService.setIndexedDbKey('currentUser', res.data);
            this.localStorageService.setKey('access_token', res.data.token);
            this.localStorageService.setKey(
              'refreshToken',
              res.data.refreshToken
            );
            this.localStorageService.setKey('roles_permissions', JSON.stringify(res.data?.roles));

            this.proceedAfterVerification(res);
          } else {
            this.popup.successAndErrorDialog('error', res?.message);
          }
        },
        error: (err: any) => {
          this.loader.hide();
          const errorMsg = err?.error?.message || 'Verification failed.';
          this.popup.successAndErrorDialog('error', errorMsg);
        },
      });
    }
  }

  private proceedAfterVerification(res: any): void {
    // Init internal chat socket immediately after login
    this.internalChatService.initSocket(res?.data?.token, res?.data?.id);
    // this.tmsSocketService.connect();
    if (res?.data?.id) {
      this.getUserDetails(res?.data?.id).subscribe({
        next: (userRes: any) => {
          this.userDetailsData = userRes?.data?.[0] || userRes?.data;
          if (!this.userDetailsData?.userDetails?.timezone) {
            this.auth.setTImezone();
          }
        },
        error: (err: any) => {
          console.error('Error fetching user details:', err);
        }
      });
    }

    // If previous day was not signed off, make user catch up before proceeding —
    // but only when there's actually something to report; otherwise skip straight
    // to start-day with a note that sign-off was missed.
    if (res.data.isPrevDaySignOff === false && this.email !== 'automation@crizac.com') {
      this.todayOverviewService.hasCatchUpSignOffData().subscribe(hasData => {
        if (hasData) {
          this.router.navigate(['/sign-off-wrapup'], { state: { isCatchUp: true } });
        } else {
          this.router.navigate(['/start-day'], { state: { missedSignOff: true } });
        }
      });
      return;
    }

    // If user has not punched in yet, send to start-day page
    if (res.data.isSignIn === false && this.email !== 'automation@crizac.com') {
      this.router.navigate(['/start-day']);
      return;
    }

    // Already signed in — mark day started and proceed
    this.tmsService.markDayStarted();

    // Check for redirect URL and navigate accordingly
    const redirectUrl = this.auth.getRedirectUrl();
    if (redirectUrl) {
      this.router.navigateByUrl(redirectUrl);
    } else {
      this.router.navigate(['/dashboard/main-dashboard']);
    }

    // This user skips /start-day entirely on this login (already punched in),
    // so it's never given a chance to prompt for MPIN setup — do it here instead.
    if (res.data?.isMpinAdded === false) {
      const mpinDialogRef = this.dialog.open(AwayStatusComponent, {
        panelClass: 'custom-dialog-container',
        width: '400px',
        disableClose: true,
        data: { context: 'login' },
      });
      // Wait for the mandatory MPIN setup to close before showing the recap —
      // both are disableClose-free/true respectively and shouldn't stack.
      mpinDialogRef.afterClosed().subscribe(() => this._openTodayOverview());
    } else {
      this._openTodayOverview();
    }
  }

  // Compact, read-only recap of today's tasks/calendar — shown every login so
  // the user has a quick reminder of what's on their plate. Only opens when
  // the API actually has something to show.
  private _openTodayOverview(): void {
    this.todayOverviewService.hasTodayData().subscribe(hasData => {
      if (!hasData) return;
      this.dialog.open(TodayOverviewDialogComponent, {
        panelClass: 'custom-dialog-container',
        width: '1100px',
        disableClose: false,
      });
    });
  }

  onInput(event: any, index: number) {
    const input = event.target;
    let value = input.value.replace(/\D/g, ''); // only digits
    this.isAllSelected = false;

    if (value.length > 1) {
      // It's a paste or autofill that bypassed the paste event
      const digits = value.slice(0, 6);
      digits.split('').forEach((digit: string, i: number) => {
        const targetIndex = index + i;
        if (targetIndex < 6) {
          this.codeArray[targetIndex] = digit;
          const inputEl = document.getElementById(`code-${targetIndex}`) as HTMLInputElement;
          if (inputEl) {
            inputEl.value = digit;
          }
        }
      });
      this.updateFormControl();
      
      const nextIndex = Math.min(index + digits.length, 5);
      const nextInput = document.getElementById(`code-${nextIndex}`) as HTMLElement;
      if (nextInput && digits.length < 6) {
        nextInput.focus();
      } else {
        const lastInput = document.getElementById(`code-5`) as HTMLElement;
        lastInput?.focus();
      }
      return;
    }

    if (value) {
      this.codeArray[index] = value;
      input.value = value; // in case we need to reflect single digit

      // Move to next box
      if (index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`) as HTMLElement;
        nextInput?.focus();
      }

      this.updateFormControl();
    } else {
      this.codeArray[index] = '';
      this.updateFormControl();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'a' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.isAllSelected = true;
      return;
    }

    if (this.isAllSelected) {
      if (event.key === 'c' && (event.ctrlKey || event.metaKey)) {
        // Let the native copy event handle it via onCopy
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        this.clearAll();
        return;
      } else if (/^\d$/.test(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        this.clearAll();
        this.codeArray[0] = event.key;
        this.updateFormControl();
        const nextInput = document.getElementById(`code-1`) as HTMLElement;
        nextInput?.focus();
        return;
      } else if (!['Control', 'Meta', 'Shift', 'Alt'].includes(event.key)) {
        this.isAllSelected = false;
      }
    }

    if (event.key === 'Backspace' && !this.isAllSelected) {
      if (input.value === '') {
        this.codeArray[index] = '';
        if (index > 0) {
          const prevInput = document.getElementById(`code-${index - 1}`) as HTMLElement;
          prevInput?.focus();
        }
      } else {
        this.codeArray[index] = '';
      }
      this.updateFormControl();
    }
  }

  clearAll() {
    this.codeArray = ['', '', '', '', '', ''];
    this.isAllSelected = false;
    this.updateFormControl();
    const firstInput = document.getElementById(`code-0`) as HTMLElement;
    firstInput?.focus();
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    this.isAllSelected = false;
    const pastedData = event.clipboardData?.getData('text') ?? '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    // Fill codeArray with pasted digits
    digits.split('').forEach((digit, i) => {
      if (i < this.codeArray.length) {
        this.codeArray[i] = digit;
        const inputEl = document.getElementById(`code-${i}`) as HTMLInputElement;
        if (inputEl) inputEl.value = digit;
      }
    });

    this.updateFormControl();

    // Focus last filled box
    const nextIndex = digits.length >= 6 ? 5 : digits.length;
    const nextInput = document.getElementById(`code-${nextIndex}`) as HTMLInputElement;
    nextInput?.focus();
  }

  onCopy(event: ClipboardEvent) {
    const code = this.codeArray.join('');
    if (code) {
      event.preventDefault();
      event.clipboardData?.setData('text/plain', code);
    }
  }

  updateFormControl() {
    const code = this.codeArray.join('');
    this.securityForm.patchValue({ code });
  }

  getUserDetails(userId: string | number) {
    return this.profileService.getUserDetailsById(userId);
  }
}